import { useEffect, useState } from 'react'
import ReactMapGL, { Marker, NavigationControl } from 'react-map-gl'
import { css } from '@emotion/react'
import getConfig from 'next/config'

import { City, Parking } from '@/service'
import { Grid, mediaColumns } from '@/styles/grid'

import CustomMarker from '@/components/parkings/CustomMarker'
import { colors, border } from '@/styles/theme'
import { spacingSection } from '@/styles/mixins'
import { mediaUp } from '@/styles/media'
import { useMarket } from '@/utils/multi-markets/context'

import { NAVIGATION_ICONS } from '@/constants/navigationAssets'

const {
  publicRuntimeConfig: { MAP_BOX_KEY }
} = getConfig()

interface UserLocation {
  latitude: number
  longitude: number
}

interface Props {
  city?: City
  parkings?: Parking[]
  overrideLocale?: string
  customButtons?: boolean
  resetMargin?: boolean
  zoom?: number
  userLocations?: UserLocation[]
}

function MapView(props: Props) {
  const {
    parkings,
    city,
    customButtons,
    resetMargin,
    zoom = 10,
    userLocations = []
  } = props

  const { market } = useMarket()

  const [active, setActive] = useState('')

  const [zoomLocal, setZoom] = useState(zoom)

  const [viewport, setViewport] = useState({
    latitude:
      city?.latitude ??
      (market === 'pl' ? 52.24786142 : market === 'ae' ? 25.196874 : 26.2),
    longitude:
      city?.longitude ??
      (market === 'pl' ? 21.0832511 : market === 'ae' ? 55.273811 : 50.529412)
  })

  useEffect(() => {
    if (
      viewport?.latitude !== city?.latitude &&
      viewport?.longitude !== city?.longitude
    ) {
      setViewport({
        latitude:
          city?.latitude ?? market === 'pl'
            ? 52.24786142
            : market === 'ae'
            ? 25.196874
            : 26.2,
        longitude:
          city?.longitude ?? market === 'pl'
            ? 21.0832511
            : market === 'ae'
            ? 55.273811
            : 50.529412
      })
    }
  }, [city])

  const handleShowPopup = (uuid: string) => {
    setActive(active === uuid ? '' : uuid)
  }

  return (
    <div id="map" css={[styles.wrapper, resetMargin && styles.wrapperNoMargin]}>
      <Grid style={{ padding: 0 }}>
        <div css={styles.content}>
          <div css={styles.contentInner}>
            <ReactMapGL
              {...viewport}
              zoom={zoomLocal}
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '28px'
              }}
              onMove={viewport => {
                setViewport(viewport.viewState)
                setZoom(viewport.viewState.zoom)
              }}
              mapStyle="mapbox://styles/mapbox/streets-v11"
              mapboxAccessToken={MAP_BOX_KEY}
              cooperativeGestures={true}
            >
              <NavigationControl showCompass={false} />
              {userLocations.length > 0 &&
                userLocations.map((location, index) => (
                  <Marker
                    key={index}
                    latitude={location?.latitude ?? 0}
                    longitude={location?.longitude ?? 0}
                  >
                    <img
                      src={NAVIGATION_ICONS.mapPin}
                      alt={`Location ${index + 1}`}
                      style={{ width: '64px', height: '64px' }}
                      loading="lazy"
                    />
                  </Marker>
                ))}
              {parkings?.map(marker => (
                <CustomMarker
                  popup={true}
                  key={marker.uuid}
                  marker={marker}
                  showPopup={active === marker.uuid}
                  onClick={handleShowPopup}
                  customButtons={customButtons}
                />
              ))}
            </ReactMapGL>
          </div>
        </div>
      </Grid>
    </div>
  )
}

const styles = {
  wrapper: css`
    ${spacingSection};
  `,
  wrapperNoMargin: css`
    margin: 0;

    ${mediaUp('lg')} {
      margin: 0;
    }
  `,
  content: css`
    ${mediaColumns({ xs: [12, 0] })};
    position: relative;
    width: 100%;
    padding-top: ${(420 / 281) * 100}%;
    background-color: ${colors.paleGrey};
    border-radius: ${border.radiusMobile};
    margin: 0 auto;

    ${mediaUp('md')} {
      padding-top: ${(720 / 1280) * 100}%;
    }

    ${mediaUp('lg')} {
      border-radius: ${border.radius};
    }

    .mapboxgl-ctrl-top-right {
      top: 50%;
      transform: translateY(-50%);
    }
  `,
  contentInner: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    html[lang^='ar'] & {
      right: 0;
      left: unset;
    }
  `,
  buttons: css`
    display: flex;
    margin: 0 auto 14px;

    a {
      margin: 0;
      margin-inline-start: 20px;
      margin-inline-end: 5px;

      &:last-of-type {
        margin: 0;
        margin-inline-start: 5px;
        margin-inline-end: 20px;
      }
    }

    ${mediaUp('lg')} {
      margin-bottom: 30px;
    }
  `
}

export default MapView
